// outpost-crm-ui/src/Financials.jsx
// DrewOS Financials — the autonomous money layer. Ingests card + bank charges,
// auto-routes each to a deal's PO or a KTLO overhead bucket, keeps a per-deal
// cost ledger, and computes true deal margin. Human-final: every charge is
// re-assignable, splittable across deals, and comps/manual costs live alongside.
// Self-contained (own palette + api()), mirroring src/Chairs.jsx. App.js imports
// FinancialsScreen and renders it. Backend: outpost_crm/financials.py (/financials/*).

import { useState, useEffect, useCallback, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

// Brand palette — local so this module is self-contained (matches App.js COLORS).
const C = {
  ink:        "#13182a",   // Sidebar Ink
  linen:      "#E8E2D5",   // page bg
  orange:     "#FF6B1A",   // Signal
  orangeSoft: "#FF8240",
  white:      "#FFFFFF",
  card:       "#FFFFFF",
  border:     "#D6D0C2",
  text:       "#0A0E1A",
  steel:      "#8C97A6",   // muted
  slate:      "#3A4452",   // body
  rowHover:   "#FFF1E8",
  success:    "#2E9E5B",
  warning:    "#C9820A",
  danger:     "#D8443C",
};
const FONT_SANS = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const FONT_MONO = "'JetBrains Mono', ui-monospace, Menlo, monospace";
const CRM_URL = "https://outpost-crm-production.up.railway.app";

const DEAL_CATS = ["COGS", "Freight", "Travel", "Training Labor",
  "Channel Discount", "Payment Fees", "Warranty Reserve", "Other"];
const KTLO_CATS = ["AI / Software", "Shipping", "Warehouse", "Insurance",
  "Subscriptions", "Marketing · Sponsorship", "Travel", "Office", "Bank Fees", "Other"];
const ALL_CATS = Array.from(new Set([...DEAL_CATS, ...KTLO_CATS]));

async function api(path, method = "GET", body = null) {
  try {
    const opts = { method, headers: { "Content-Type": "application/json" } };
    if (body) opts.body = JSON.stringify(body);
    const r = await fetch(`${CRM_URL}${path}`, opts);
    if (!r.ok) {
      let msg = `${r.status}`;
      try { const j = await r.json(); msg = j.detail || msg; } catch {}
      throw new Error(msg);
    }
    if (r.status === 204) return true;
    return await r.json();
  } catch (e) {
    console.error("Financials API error:", path, e.message);
    return null;
  }
}

const usd = (n) =>
  (n == null || isNaN(n)) ? "—"
    : "$" + Math.round(Math.abs(n)).toLocaleString("en-US") + (n < 0 ? " cr" : "");
const usd2 = (n) =>
  (n == null || isNaN(n)) ? "—"
    : "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── shared bits ─────────────────────────────────────────────────────────────
const card = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 6, padding: 20,
};
const label = {
  fontFamily: FONT_MONO, fontSize: 10, color: C.orange, letterSpacing: "0.16em",
  textTransform: "uppercase", fontWeight: 600,
};
const mono = { fontFamily: FONT_MONO };

function Btn({ variant = "primary", children, style, ...rest }) {
  const base = {
    padding: "9px 16px", borderRadius: 4, cursor: "pointer", fontFamily: FONT_MONO,
    fontWeight: 600, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
    transition: "opacity .12s, background .12s", border: "none", whiteSpace: "nowrap",
  };
  const v = variant === "primary"
    ? { background: C.orange, color: C.text }
    : variant === "ghost"
      ? { background: "transparent", color: C.slate, border: `1px solid ${C.border}` }
      : { background: C.white, color: C.text, border: `1px solid ${C.border}` };
  return (
    <button {...rest} style={{ ...base, ...v, ...style }}
      onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.85)}
      onMouseLeave={(e) => (e.currentTarget.style.opacity = 1)}>
      {children}
    </button>
  );
}

function Pill({ tone, children }) {
  const map = {
    auto:   [C.success, "rgba(46,158,91,.12)"],
    review: [C.warning, "rgba(201,130,10,.14)"],
    manual: [C.orange, "rgba(255,107,26,.12)"],
    split:  ["#6C5CE7", "rgba(108,92,231,.12)"],
    deal:   [C.orange, "rgba(255,107,26,.12)"],
    ktlo:   [C.steel, "rgba(140,151,166,.16)"],
  };
  const [fg, bg] = map[tone] || [C.steel, "rgba(140,151,166,.16)"];
  return (
    <span style={{
      ...mono, fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
      color: fg, background: bg, padding: "3px 8px", borderRadius: 3, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Kpi({ label: l, value, sub, tone }) {
  return (
    <div style={{ ...card, padding: "16px 18px" }}>
      <div style={label}>{l}</div>
      <div style={{ fontFamily: FONT_SANS, fontSize: 28, fontWeight: 800, color: tone || C.text, lineHeight: 1.05, marginTop: 8, letterSpacing: "-0.02em" }}>{value}</div>
      {sub && <div style={{ ...mono, fontSize: 10, color: C.steel, marginTop: 6, letterSpacing: "0.04em" }}>{sub}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function FinancialsScreen() {
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [deals, setDeals] = useState([]);
  const [toast, setToast] = useState(null);
  const fileRef = useRef(null);

  const flash = useCallback((msg, bad) => {
    setToast({ msg, bad });
    setTimeout(() => setToast(null), 3200);
  }, []);

  const loadTop = useCallback(async () => {
    const [s, d] = await Promise.all([api("/financials/summary"), api("/financials/deals")]);
    if (s) setSummary(s);
    if (d?.deals) setDeals(d.deals);
  }, []);
  useEffect(() => { loadTop(); }, [loadTop]);

  const dealsByPO = deals.filter((d) => (d.po_number || "").trim());

  async function importCsv(file, sourceLabel) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${CRM_URL}/financials/import?source_label=${encodeURIComponent(sourceLabel || "Capital One")}`,
        { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || r.status);
      flash(`Imported ${j.imported} · ${j.needs_review} need review · ${j.skipped_duplicates} dupes skipped`);
      loadTop();
    } catch (e) { flash("Import failed: " + e.message, true); }
  }

  const TABS = [
    ["overview", "Overview"],
    ["transactions", "Transactions"],
    ["overhead", "Overhead · KTLO"],
    ["pnl", "Deal P&L"],
    ["travel", "Travel"],
    ["sources", "Sources & Rules"],
  ];

  return (
    <div style={{ fontFamily: FONT_SANS, color: C.text }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontFamily: FONT_SANS, fontSize: 24, fontWeight: 800, letterSpacing: "-0.01em" }}>Financials</div>
          <div style={{ ...mono, fontSize: 11, color: C.steel, marginTop: 4, letterSpacing: "0.04em" }}>
            Autonomous money engine · every charge routed to a PO or KTLO bucket
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }}
            onChange={(e) => { importCsv(e.target.files[0], "Capital One Spark *8306"); e.target.value = ""; }} />
          <Btn variant="secondary" onClick={() => fileRef.current?.click()}>Import CSV</Btn>
          <Btn variant="ghost" onClick={async () => { const r = await api("/financials/route-all", "POST"); flash(`Re-routed ${r?.re_routed ?? 0} charge(s)`); loadTop(); }}>Route All</Btn>
        </div>
      </div>

      {/* sub-nav */}
      <div style={{ display: "flex", gap: 4, marginBottom: 18, flexWrap: "wrap" }}>
        {TABS.map(([id, l]) => (
          <div key={id} onClick={() => setTab(id)} style={{
            ...mono, fontSize: 11, fontWeight: tab === id ? 700 : 500, letterSpacing: "0.08em",
            textTransform: "uppercase", padding: "8px 14px", cursor: "pointer", borderRadius: 4,
            color: tab === id ? C.text : C.steel,
            background: tab === id ? C.white : "transparent",
            border: `1px solid ${tab === id ? C.border : "transparent"}`,
          }}>{l}
            {id === "transactions" && summary?.needs_review > 0 &&
              <span style={{ marginLeft: 7, background: C.warning, color: C.white, borderRadius: 8, padding: "1px 6px", fontSize: 9 }}>{summary.needs_review}</span>}
          </div>
        ))}
      </div>

      {tab === "overview" && <Overview summary={summary} onReview={() => setTab("transactions")} flash={flash} />}
      {tab === "transactions" && <Transactions deals={deals} onChange={loadTop} flash={flash} />}
      {tab === "overhead" && <Overhead />}
      {tab === "pnl" && <DealPnl deals={deals} dealsByPO={dealsByPO} reloadDeals={loadTop} flash={flash} />}
      {tab === "travel" && <Travel flash={flash} />}
      {tab === "sources" && <SourcesRules deals={dealsByPO} flash={flash} onImport={() => fileRef.current?.click()} />}

      {toast &&
        <div style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 9999,
          background: toast.bad ? C.danger : C.ink, color: C.white,
          padding: "12px 18px", borderRadius: 6, ...mono, fontSize: 12,
          boxShadow: "0 8px 30px rgba(0,0,0,.25)", maxWidth: 380,
        }}>{toast.msg}</div>}
    </div>
  );
}

// ─── Overview ────────────────────────────────────────────────────────────────
function Overview({ summary, onReview, flash }) {
  const [roll, setRoll] = useState(null);
  useEffect(() => { api("/financials/rollup").then(setRoll); }, []);
  const s = summary;
  const chartData = (roll?.categories || []).slice(0, 7).map((c) => ({ name: c.category, v: c.amount, trend: c.trend_pct }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 12 }}>
        <Kpi label={`Spend · ${s?.month || ""}`} value={usd(s?.spend?.total)} sub={`${s?.spend?.count || 0} charges`} />
        <Kpi label="Deal-Attributable" value={usd(s?.deal_attributable?.total)} sub={`${s?.deal_attributable?.count || 0} tied to a PO`} tone={C.orange} />
        <Kpi label="Overhead · KTLO" value={usd(s?.overhead_ktlo?.total)} sub={`${s?.overhead_ktlo?.count || 0} keep-the-lights-on`} />
        <Kpi label="Needs Review" value={s?.needs_review ?? "—"} sub={s?.needs_review ? "AI unsure — confirm" : "all routed"} tone={s?.needs_review ? C.warning : C.success} />
      </div>

      {s?.needs_review > 0 &&
        <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", borderLeft: `3px solid ${C.warning}` }}>
          <div style={{ ...mono, fontSize: 12, color: C.slate }}>
            <b style={{ color: C.text }}>{s.needs_review} charge(s)</b> the router couldn't place. Confirm the deal or KTLO bucket.
          </div>
          <Btn onClick={onReview}>Review now →</Btn>
        </div>}

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
          <div style={label}>Overhead trend · {roll?.month} vs {roll?.prev_month}</div>
          <div style={{ ...mono, fontSize: 10, color: C.steel }}>keep-the-lights-on by category</div>
        </div>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: FONT_MONO, fill: C.steel }} axisLine={{ stroke: C.border }} tickLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10, fontFamily: FONT_MONO, fill: C.steel }} axisLine={false} tickLine={false} tickFormatter={usd} width={54} />
              <Tooltip cursor={{ fill: C.rowHover }} formatter={(v, _n, p) => [usd2(v) + (p.payload.trend ? `  (${p.payload.trend > 0 ? "+" : ""}${p.payload.trend}% MoM)` : ""), "spend"]}
                contentStyle={{ fontFamily: FONT_MONO, fontSize: 11, borderRadius: 6, border: `1px solid ${C.border}` }} />
              <Bar dataKey="v" radius={[4, 4, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={d.trend > 25 ? C.danger : C.orange} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty>No overhead spend yet this month. Import a statement to begin.</Empty>}
      </div>
    </div>
  );
}

// ─── Transactions ────────────────────────────────────────────────────────────
function Transactions({ deals, onChange, flash }) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // txn being routed
  const [splitFor, setSplitFor] = useState(null);

  const load = useCallback(async () => {
    const q = filter === "review" ? "?status=review" : filter === "deal" ? "?scope=deal" : filter === "ktlo" ? "?scope=ktlo" : "";
    const r = await api(`/financials/transactions${q}`);
    setRows(r?.transactions || []);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  async function saveRoute(txn, patch, learn) {
    const r = await api(`/financials/transactions/${txn.id}`, "PATCH", { ...patch, learn: !!learn });
    if (r) { flash(`Routed · ${r.deal_label || r.category || "KTLO"}`); setEditing(null); load(); onChange?.(); }
    else flash("Route failed", true);
  }
  async function saveSplit(txn, splits) {
    const r = await api(`/financials/transactions/${txn.id}`, "PATCH", { splits });
    if (r) { flash("Split across " + splits.length + " deals"); setSplitFor(null); load(); onChange?.(); }
    else flash("Split failed", true);
  }

  const FILTERS = [["all", "All"], ["review", "Needs review"], ["deal", "On a PO"], ["ktlo", "Overhead"]];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {FILTERS.map(([id, l]) => (
          <div key={id} onClick={() => setFilter(id)} style={{
            ...mono, fontSize: 10, fontWeight: filter === id ? 700 : 500, letterSpacing: "0.08em",
            textTransform: "uppercase", padding: "6px 11px", cursor: "pointer", borderRadius: 3,
            color: filter === id ? C.text : C.steel, background: filter === id ? C.white : "transparent",
            border: `1px solid ${filter === id ? C.border : "transparent"}`,
          }}>{l}</div>
        ))}
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        {rows == null ? <Empty>Loading…</Empty>
          : rows.length === 0 ? <Empty>No transactions. Import a card or bank statement CSV to begin.</Empty>
            : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ ...mono, fontSize: 9, color: C.steel, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      {["Date", "Merchant", "Amount", "Routed to", "Category", "Status", ""].map((h, i) => (
                        <th key={i} style={{ textAlign: i === 2 ? "right" : "left", padding: "10px 14px", borderBottom: `1px solid ${C.border}`, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr key={t.id} style={{ borderBottom: `1px solid ${C.linen}` }}
                        onMouseEnter={(e) => e.currentTarget.style.background = C.rowHover}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                        <td style={{ ...mono, padding: "9px 14px", color: C.steel, whiteSpace: "nowrap" }}>{(t.txn_date || "").slice(5) || "—"}</td>
                        <td style={{ padding: "9px 14px", maxWidth: 260 }}>
                          <div style={{ fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.merchant}</div>
                          {t.source_label && <div style={{ ...mono, fontSize: 9, color: C.steel }}>{t.source_label}{t.recurring ? " · recurring" : ""}</div>}
                        </td>
                        <td style={{ ...mono, padding: "9px 14px", textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>{usd2(t.amount_usd)}</td>
                        <td style={{ padding: "9px 14px", maxWidth: 220 }}>
                          {t.deal_label
                            ? <span style={{ ...mono, fontSize: 11, color: C.orange, fontWeight: 600 }}>{t.deal_label}</span>
                            : t.status === "split" ? <Pill tone="split">split · multi-deal</Pill>
                              : t.status === "review" ? <Pill tone="review">unassigned</Pill>
                                : <Pill tone="ktlo">overhead</Pill>}
                        </td>
                        <td style={{ ...mono, padding: "9px 14px", color: C.slate, whiteSpace: "nowrap" }}>{t.category || "—"}</td>
                        <td style={{ padding: "9px 14px" }}><Pill tone={t.status}>{t.status}</Pill></td>
                        <td style={{ padding: "9px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                          <span onClick={() => setEditing(editing?.id === t.id ? null : t)} style={{ ...mono, fontSize: 10, color: C.orange, cursor: "pointer", fontWeight: 700, marginRight: 12 }}>ROUTE</span>
                          <span onClick={() => setSplitFor(t)} style={{ ...mono, fontSize: 10, color: C.steel, cursor: "pointer", fontWeight: 700 }}>SPLIT</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>

      {editing && <RouteEditor txn={editing} deals={deals} onSave={saveRoute} onClose={() => setEditing(null)} />}
      {splitFor && <SplitEditor txn={splitFor} deals={deals} onSave={saveSplit} onClose={() => setSplitFor(null)} />}
    </div>
  );
}

function RouteEditor({ txn, deals, onSave, onClose }) {
  const [dealId, setDealId] = useState(txn.deal_id || "");
  const [cat, setCat] = useState(txn.category || "");
  const [learn, setLearn] = useState(false);
  const isDeal = !!dealId;
  return (
    <Modal onClose={onClose} title={`Route · ${txn.merchant}`} sub={usd2(txn.amount_usd)}>
      <Field label="Attach to deal (by PO)">
        <select value={dealId} onChange={(e) => { setDealId(e.target.value ? +e.target.value : ""); if (e.target.value) setCat((c) => c || "COGS"); }} style={selStyle}>
          <option value="">— None · keep as overhead —</option>
          {deals.map((d) => (
            <option key={d.id} value={d.id}>{d.label || d.title}{d.value ? `  (${usd(d.value)})` : ""}</option>
          ))}
        </select>
      </Field>
      <Field label="Category">
        <select value={cat} onChange={(e) => setCat(e.target.value)} style={selStyle}>
          <option value="">—</option>
          {(isDeal ? DEAL_CATS : KTLO_CATS).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <label style={{ display: "flex", alignItems: "center", gap: 8, ...mono, fontSize: 11, color: C.slate, cursor: "pointer", marginTop: 4 }}>
        <input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} />
        Remember this — auto-route "{(txn.merchant || "").split(" ")[0]}…" like this next time
      </label>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(txn, { deal_id: dealId || null, category: cat || null, status: "manual", is_deal_cost: isDeal }, learn)}>Save routing</Btn>
      </div>
    </Modal>
  );
}

function SplitEditor({ txn, deals, onSave, onClose }) {
  const [rows, setRows] = useState([{ deal_id: "", amount: "", category: "COGS" }]);
  const total = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const set = (i, k, v) => setRows((rs) => rs.map((r, j) => j === i ? { ...r, [k]: v } : r));
  return (
    <Modal onClose={onClose} title={`Split · ${txn.merchant}`} sub={`${usd2(txn.amount_usd)} across deals`}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 90px 110px 28px", gap: 8, marginBottom: 8, alignItems: "center" }}>
          <select value={r.deal_id} onChange={(e) => set(i, "deal_id", e.target.value)} style={selStyle}>
            <option value="">— deal —</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.label || d.title}</option>)}
          </select>
          <input type="number" placeholder="$" value={r.amount} onChange={(e) => set(i, "amount", e.target.value)} style={selStyle} />
          <select value={r.category} onChange={(e) => set(i, "category", e.target.value)} style={selStyle}>
            {DEAL_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <span onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} style={{ cursor: "pointer", color: C.danger, textAlign: "center", ...mono }}>✕</span>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <span onClick={() => setRows((rs) => [...rs, { deal_id: "", amount: "", category: "COGS" }])} style={{ ...mono, fontSize: 10, color: C.orange, cursor: "pointer", fontWeight: 700 }}>+ ADD DEAL</span>
        <span style={{ ...mono, fontSize: 11, color: Math.abs(total - txn.amount_usd) < 0.01 ? C.success : C.warning }}>
          allocated {usd2(total)} / {usd2(txn.amount_usd)}
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(txn, rows.filter((r) => r.deal_id && parseFloat(r.amount) > 0).map((r) => ({ deal_id: +r.deal_id, amount: parseFloat(r.amount), category: r.category })))}>Save split</Btn>
      </div>
    </Modal>
  );
}

// ─── Overhead / KTLO ─────────────────────────────────────────────────────────
function Overhead() {
  const [roll, setRoll] = useState(null);
  useEffect(() => { api("/financials/rollup").then(setRoll); }, []);
  const cats = roll?.categories || [];
  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
        <div style={label}>Keep-the-lights-on · {roll?.month}</div>
        <div style={{ ...mono, fontSize: 10, color: C.steel }}>vs {roll?.prev_month}</div>
      </div>
      {cats.length === 0 ? <Empty>No overhead charges yet this month.</Empty> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {cats.map((c) => (
            <div key={c.category} style={{ display: "grid", gridTemplateColumns: "1fr 110px 90px", alignItems: "center", padding: "10px 4px", borderBottom: `1px solid ${C.linen}` }}>
              <div style={{ ...mono, fontSize: 12, color: C.text, fontWeight: 600 }}>{c.category}</div>
              <div style={{ ...mono, fontSize: 13, textAlign: "right", fontWeight: 700 }}>{usd2(c.amount)}</div>
              <div style={{ ...mono, fontSize: 11, textAlign: "right", color: c.trend_pct > 0 ? C.danger : c.trend_pct < 0 ? C.success : C.steel }}>
                {c.trend_pct > 0 ? "▲" : c.trend_pct < 0 ? "▼" : "–"} {Math.abs(c.trend_pct)}%
              </div>
            </div>
          ))}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 110px 90px", padding: "12px 4px 2px", marginTop: 6, borderTop: `2px solid ${C.border}` }}>
            <div style={{ ...mono, fontSize: 11, color: C.steel, textTransform: "uppercase", letterSpacing: "0.1em" }}>Total overhead</div>
            <div style={{ ...mono, fontSize: 14, textAlign: "right", fontWeight: 800, color: C.orange }}>{usd2(cats.reduce((s, c) => s + c.amount, 0))}</div>
            <div />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Deal P&L ────────────────────────────────────────────────────────────────
function DealPnl({ deals, dealsByPO, reloadDeals, flash }) {
  const [dealId, setDealId] = useState(dealsByPO[0]?.id || deals[0]?.id || "");
  const [pnl, setPnl] = useState(null);
  const [poEdit, setPoEdit] = useState("");
  const [showCost, setShowCost] = useState(false);

  const load = useCallback(async (id) => {
    if (!id) { setPnl(null); return; }
    const p = await api(`/financials/deals/${id}/pnl`);
    setPnl(p);
    setPoEdit(p?.po_number || "");
  }, []);
  useEffect(() => { load(dealId); }, [dealId, load]);

  async function savePO() {
    const r = await api(`/financials/deals/${dealId}/po`, "PATCH", { po_number: poEdit });
    if (r) { flash(`PO set · re-routed ${r.re_routed} charge(s)`); load(dealId); reloadDeals?.(); }
    else flash("Could not set PO", true);
  }
  async function addCost(c) {
    const r = await api("/financials/deal-costs", "POST", { ...c, deal_id: +dealId });
    if (r) { flash("Cost added to PO"); setShowCost(false); load(dealId); reloadDeals?.(); }
    else flash("Add cost failed", true);
  }
  async function delCost(id) {
    await api(`/financials/deal-costs/${id}`, "DELETE");
    load(dealId); reloadDeals?.();
  }

  const margin = pnl?.margin_pct;
  const marginTone = margin == null ? C.steel : margin >= 30 ? C.success : margin >= 15 ? C.warning : C.danger;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ ...card, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ ...label, marginRight: 4 }}>Deal</div>
        <select value={dealId} onChange={(e) => setDealId(e.target.value ? +e.target.value : "")} style={{ ...selStyle, minWidth: 320, flex: 1 }}>
          <option value="">— pick a deal —</option>
          {deals.map((d) => <option key={d.id} value={d.id}>{d.label || d.title}{d.value ? `  (${usd(d.value)})` : ""}</option>)}
        </select>
      </div>

      {pnl && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: 12 }}>
            <Kpi label="Revenue" value={usd(pnl.revenue)} sub={pnl.po_number ? `PO ${pnl.po_number}` : "no PO set"} />
            <Kpi label="True Cost" value={usd(pnl.total_cost)} sub={`${pnl.costs.length} line item(s)`} />
            <Kpi label="Net Profit" value={usd(pnl.net_profit)} tone={pnl.net_profit >= 0 ? C.success : C.danger} />
            <Kpi label="Margin" value={margin == null ? "—" : margin + "%"} tone={marginTone} sub={margin >= 30 ? "healthy" : margin >= 15 ? "watch" : "thin"} />
          </div>

          {/* PO spine editor */}
          <div style={{ ...card, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={label}>PO # (routing spine)</div>
            <input value={poEdit} onChange={(e) => setPoEdit(e.target.value)} placeholder="e.g. 23059" style={{ ...selStyle, width: 160 }} />
            <Btn variant="secondary" onClick={savePO}>Set PO</Btn>
            <div style={{ ...mono, fontSize: 10, color: C.steel }}>Any imported charge naming this PO auto-attaches here.</div>
          </div>

          {/* cost ledger */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
              <div style={label}>Cost ledger</div>
              <Btn variant="ghost" onClick={() => setShowCost(true)}>+ Add cost / comp</Btn>
            </div>
            {pnl.costs.length === 0 ? <Empty>No costs yet. Add a comp (e.g. free ProMove sling) or route a charge to this PO.</Empty> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <tbody>
                  {pnl.costs.map((c) => {
                    const amt = c.amount_actual || c.amount_est || 0;
                    return (
                      <tr key={c.id} style={{ borderBottom: `1px solid ${C.linen}` }}>
                        <td style={{ padding: "10px 18px" }}>
                          <span style={{ fontWeight: 600 }}>{c.description}</span>
                          <span style={{ ...mono, fontSize: 9, color: C.steel, marginLeft: 8, textTransform: "uppercase" }}>{c.source}</span>
                        </td>
                        <td style={{ ...mono, padding: "10px 14px", color: C.slate }}>{c.category}</td>
                        <td style={{ ...mono, padding: "10px 18px", textAlign: "right", fontWeight: 700, whiteSpace: "nowrap" }}>{usd2(amt)}</td>
                        <td style={{ padding: "10px 14px", textAlign: "right", width: 30 }}>
                          {c.source === "manual" && <span onClick={() => delCost(c.id)} style={{ cursor: "pointer", color: C.steel, ...mono }}>✕</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {showCost && <CostEditor onSave={addCost} onClose={() => setShowCost(false)} />}
    </div>
  );
}

function CostEditor({ onSave, onClose }) {
  const [f, setF] = useState({ description: "", category: "COGS", amount_actual: "", vendor: "" });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  return (
    <Modal onClose={onClose} title="Add cost / comp to this PO" sub="in-kind, comps, or any non-card cost">
      <Field label="What is it?"><input value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="e.g. Free ProMove sling (comp)" style={selStyle} /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Category">
          <select value={f.category} onChange={(e) => set("category", e.target.value)} style={selStyle}>
            {DEAL_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Cost (COG) $"><input type="number" value={f.amount_actual} onChange={(e) => set("amount_actual", e.target.value)} placeholder="288" style={selStyle} /></Field>
      </div>
      <Field label="Vendor (optional)"><input value={f.vendor} onChange={(e) => set("vendor", e.target.value)} style={selStyle} /></Field>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave({ description: f.description || "Cost", category: f.category, amount_actual: parseFloat(f.amount_actual) || 0, vendor: f.vendor || null })}>Add to PO</Btn>
      </div>
    </Modal>
  );
}

// ─── Sources & Rules ─────────────────────────────────────────────────────────
function SourcesRules({ deals, flash, onImport }) {
  const [sources, setSources] = useState([]);
  const [rules, setRules] = useState([]);
  const [newSrc, setNewSrc] = useState({ name: "", kind: "card", last4: "" });
  const [newRule, setNewRule] = useState({ match_pattern: "", category: "AI / Software", deal_id: "", is_deal_cost: false });

  const load = useCallback(async () => {
    const [s, r] = await Promise.all([api("/financials/sources"), api("/financials/rules")]);
    setSources(s?.sources || []); setRules(r?.rules || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function addSource() {
    if (!newSrc.name) return;
    const r = await api("/financials/sources", "POST", newSrc);
    if (r) { flash("Source added"); setNewSrc({ name: "", kind: "card", last4: "" }); load(); }
  }
  async function addRule() {
    if (!newRule.match_pattern) return;
    const r = await api("/financials/rules", "POST", { ...newRule, deal_id: newRule.deal_id ? +newRule.deal_id : null, priority: 10 });
    if (r) { flash("Rule added"); setNewRule({ match_pattern: "", category: "AI / Software", deal_id: "", is_deal_cost: false }); load(); }
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 16 }}>
      {/* sources */}
      <div style={card}>
        <div style={{ ...label, marginBottom: 12 }}>Funding sources</div>
        {sources.length === 0 && <div style={{ ...mono, fontSize: 11, color: C.steel, marginBottom: 12 }}>No sources yet. Add your Capital One Spark card and bank account.</div>}
        {sources.map((s) => (
          <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: `1px solid ${C.linen}` }}>
            <div><span style={{ fontWeight: 600 }}>{s.name}</span>{s.last4 && <span style={{ ...mono, fontSize: 10, color: C.steel }}> ····{s.last4}</span>}</div>
            <Pill tone={s.kind === "bank" ? "ktlo" : "deal"}>{s.kind}</Pill>
          </div>
        ))}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 70px", gap: 8, marginTop: 12 }}>
          <input placeholder="Name (Spark Miles)" value={newSrc.name} onChange={(e) => setNewSrc({ ...newSrc, name: e.target.value })} style={selStyle} />
          <select value={newSrc.kind} onChange={(e) => setNewSrc({ ...newSrc, kind: e.target.value })} style={selStyle}><option value="card">card</option><option value="bank">bank</option></select>
          <input placeholder="last4" value={newSrc.last4} onChange={(e) => setNewSrc({ ...newSrc, last4: e.target.value })} style={selStyle} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <Btn variant="secondary" onClick={addSource}>Add source</Btn>
          <Btn variant="ghost" onClick={onImport}>Import CSV →</Btn>
        </div>
        <div style={{ ...mono, fontSize: 10, color: C.steel, marginTop: 12, lineHeight: 1.5 }}>
          Phase 2: live Plaid / Capital One feed replaces manual CSV import. Bank ACH sources catch recurring items (e.g. $300/mo Hiro Chronicles sponsorship).
        </div>
      </div>

      {/* rules */}
      <div style={card}>
        <div style={{ ...label, marginBottom: 12 }}>Auto-route rules</div>
        <div style={{ ...mono, fontSize: 10, color: C.steel, marginBottom: 12 }}>Custom rules run before the built-in seed router. "Remember this" on any charge writes one here.</div>
        {rules.length === 0 && <div style={{ ...mono, fontSize: 11, color: C.steel, marginBottom: 12 }}>No custom rules — the built-in router (FedEx→Shipping, Anthropic→AI, HALO→COGS…) is active.</div>}
        {rules.map((r) => (
          <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.linen}` }}>
            <code style={{ ...mono, fontSize: 11, color: C.slate }}>/{r.match_pattern}/</code>
            <span style={{ ...mono, fontSize: 10, color: C.orange }}>{r.category}{r.is_deal_cost ? " · deal" : ""}</span>
          </div>
        ))}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
          <input placeholder="merchant match (regex ok)" value={newRule.match_pattern} onChange={(e) => setNewRule({ ...newRule, match_pattern: e.target.value })} style={selStyle} />
          <select value={newRule.category} onChange={(e) => setNewRule({ ...newRule, category: e.target.value })} style={selStyle}>
            {ALL_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ marginTop: 10 }}><Btn variant="secondary" onClick={addRule}>Add rule</Btn></div>
      </div>
    </div>
  );
}

// ─── Travel (estimate + expenses, localStorage) ──────────────────────────────
const TRAVEL_KEY = "drewos_travel_v1";
const T_COLORS = ["#FF6B1A", "#F2B84B", "#2E9E5B", "#5B9DF0", "#9B8CFF", "#F2789B", "#8C97A6"];
const PRESETS = {
  "Kansas City / Topeka, KS": { flight: 650, hotel: 150, meals: 65, car: 90 },
  "Los Angeles, CA (drive)": { flight: 0, hotel: 200, meals: 70, car: 0 },
  "San Francisco Bay Area, CA": { flight: 180, hotel: 290, meals: 85, car: 75 },
  "New York, NY": { flight: 380, hotel: 330, meals: 90, car: 0 },
  "Washington, DC": { flight: 360, hotel: 270, meals: 85, car: 0 },
  "Denver, CO": { flight: 250, hotel: 200, meals: 75, car: 70 },
  "Chicago, IL": { flight: 300, hotel: 245, meals: 80, car: 60 },
  "Dallas / Ft. Worth, TX": { flight: 260, hotel: 190, meals: 72, car: 70 },
  "Phoenix, AZ": { flight: 150, hotel: 180, meals: 70, car: 65 },
  "Seattle, WA": { flight: 200, hotel: 260, meals: 82, car: 70 },
  "Custom / other": { flight: 400, hotel: 210, meals: 72, car: 75 },
};
const uid = () => "t" + Math.random().toString(36).slice(2, 9);
const mkItem = (date, cat, item, est, act, paid) => ({ id: uid(), date, cat, item, est, act, paid });
const seedTrips = () => ([{
  id: "kdol", name: "KDOL Training — Topeka, KS", dest: "Kansas City / Topeka, KS",
  items: [
    mkItem("2026-08-12", "Flight", "Alaska RT SAN–MCI (Capital One)", 706.40, 706.40, "Spark Miles"),
    mkItem("2026-08-12", "Hotel", "Cyrus Hotel — Wed (#89884196)", 181.25, 181.25, "MasterCard"),
    mkItem("2026-08-13", "Hotel", "Cyrus Hotel — Thu (#89825468)", 131.38, 131.38, "MasterCard"),
    mkItem("2026-08-12", "Rental Car", "Alamo Nissan Versa @ MCI", 150.00, 185.94, "Spark Miles"),
    mkItem("2026-08-14", "Fuel", "Rental gas", 25.00, "", ""),
    mkItem("2026-08-12", "Parking", "Cyrus $14/day × 2", 28.00, "", ""),
    mkItem("2026-08-10", "Shipping", "Ship HALO Swift FedEx Ground", 45.00, "", ""),
    mkItem("2026-08-13", "Meals", "Thu — training day", 65.00, "", ""),
    mkItem("2026-08-12", "Meals", "Wed — travel day", 30.00, "", ""),
    mkItem("2026-08-14", "Meals", "Fri — travel day", 25.00, "", ""),
    mkItem("2026-08-12", "Incidentals", "Tips / misc", 30.00, "", ""),
  ],
}]);

function Travel({ flash }) {
  const [mode, setMode] = useState("estimate");
  const [state, setState] = useState(() => {
    try { const r = localStorage.getItem(TRAVEL_KEY); if (r) return JSON.parse(r); } catch {}
    return { activeTripId: "kdol", trips: seedTrips() };
  });
  useEffect(() => { try { localStorage.setItem(TRAVEL_KEY, JSON.stringify(state)); } catch {} }, [state]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "inline-flex", gap: 4, background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: 4, alignSelf: "flex-start" }}>
        {[["estimate", "◇ Estimate"], ["expenses", "▤ Expenses"]].map(([id, l]) => (
          <div key={id} onClick={() => setMode(id)} style={{
            ...mono, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", padding: "8px 16px",
            borderRadius: 6, cursor: "pointer", textTransform: "uppercase",
            background: mode === id ? C.orange : "transparent", color: mode === id ? C.text : C.steel,
          }}>{l}</div>
        ))}
      </div>
      {mode === "estimate"
        ? <Estimator onSave={(trip) => { setState((s) => ({ activeTripId: trip.id, trips: [trip, ...s.trips] })); setMode("expenses"); flash("Saved to Expenses"); }} />
        : <Expenses state={state} setState={setState} />}
    </div>
  );
}

function Estimator({ onSave }) {
  const [dest, setDest] = useState("Kansas City / Topeka, KS");
  const [origin, setOrigin] = useState("San Diego (SAN)");
  const [nights, setNights] = useState(2);
  const [f, setF] = useState({ ...PRESETS["Kansas City / Topeka, KS"], park: 15, fuel: 25, incid: 30 });
  const applyPreset = (d) => { const p = PRESETS[d]; setF((s) => ({ ...s, ...p })); };
  const g = (k) => +f[k] || 0;
  const n = Math.max(0, +nights || 0), td = n + 1;
  const rows = [
    { k: "Flight", v: g("flight"), s: "round trip" },
    { k: "Hotel", v: g("hotel") * n, s: `$${g("hotel")}/night × ${n}` },
    { k: "Meals", v: g("meals") * td, s: `$${g("meals")}/day × ${td}` },
    { k: "Rental car", v: g("car") * td, s: g("car") > 0 ? `$${g("car")}/day × ${td}` : "—" },
    { k: "Fuel", v: g("car") > 0 ? g("fuel") : 0, s: g("car") > 0 ? "trip" : "—" },
    { k: "Parking", v: g("park") * n, s: `$${g("park")}/night × ${n}` },
    { k: "Incidentals", v: g("incid") * td, s: `$${g("incid")}/day × ${td}` },
  ];
  const total = rows.reduce((a, r) => a + r.v, 0);

  // plain function (not a nested component) so inputs keep focus across renders
  const inp = (k, l, dollar) => (
    <div key={k} style={{ position: "relative", flex: 1 }}>
      <div style={{ ...mono, fontSize: 10, color: C.steel, marginBottom: 5 }}>{l}</div>
      {dollar && <span style={{ position: "absolute", left: 9, top: 27, ...mono, fontSize: 12, color: C.steel }}>$</span>}
      <input type="number" value={f[k]} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))}
        style={{ ...selStyle, paddingLeft: dollar ? 18 : 10 }} />
    </div>
  );

  function save() {
    const items = [
      mkItem("", "Flight", "Round-trip airfare", g("flight"), "", ""),
      mkItem("", "Hotel", `Lodging (${n} nights)`, g("hotel") * n, "", ""),
      mkItem("", "Meals", `Meals (${td} days)`, g("meals") * td, "", ""),
    ];
    if (g("car") > 0) { items.push(mkItem("", "Rental Car", `Rental car (${td} days)`, g("car") * td, "", "")); items.push(mkItem("", "Fuel", "Fuel", g("fuel"), "", "")); }
    items.push(mkItem("", "Parking", `Parking (${n} nights)`, g("park") * n, "", ""));
    items.push(mkItem("", "Incidentals", `Incidentals (${td} days)`, g("incid") * td, "", ""));
    onSave({ id: uid(), name: dest.replace(/\s*\(.*\)/, "") + " — trip", dest, items });
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.05fr", gap: 16, alignItems: "start" }}>
      <div style={card}>
        <div style={label}>Trip inputs</div>
        <div style={{ marginTop: 12 }}>
          <div style={{ ...mono, fontSize: 10, color: C.steel, marginBottom: 5 }}>Destination</div>
          <select value={dest} onChange={(e) => { setDest(e.target.value); applyPreset(e.target.value); }} style={selStyle}>
            {Object.keys(PRESETS).map((k) => <option key={k}>{k}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ ...mono, fontSize: 10, color: C.steel, marginBottom: 5 }}>Origin</div>
            <input value={origin} onChange={(e) => setOrigin(e.target.value)} style={selStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ ...mono, fontSize: 10, color: C.steel, marginBottom: 5 }}>Nights on the ground</div>
            <input type="number" min="0" value={nights} onChange={(e) => setNights(e.target.value)} style={selStyle} />
          </div>
        </div>
        <div style={{ ...label, marginTop: 18, marginBottom: 4 }}>Cost assumptions · edit anything</div>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>{inp("flight", "Flight (round trip)", true)}{inp("hotel", "Hotel / night", true)}</div>
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>{inp("meals", "Meals / day", true)}{inp("car", "Rental car / day", true)}</div>
        <div style={{ display: "flex", gap: 10, marginTop: 12 }}>{inp("park", "Parking / night", true)}{inp("fuel", "Fuel (trip)", true)}{inp("incid", "Incidentals / day", true)}</div>
        <div style={{ ...mono, fontSize: 10, color: C.steel, marginTop: 12 }}>Presets fill smart defaults from San Diego — tweak any field for real numbers.</div>
      </div>

      <div style={card}>
        <div style={label}>Estimated all-in</div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginTop: 8 }}>
          <div style={{ ...mono, fontWeight: 800, fontSize: 42, letterSpacing: "-0.03em", lineHeight: 1 }}>{usd2(total).split(".")[0]}<span style={{ color: C.steel, fontSize: 22 }}>.{usd2(total).split(".")[1]}</span></div>
          <div style={{ textAlign: "right" }}><b style={{ ...mono, color: C.orangeSoft, fontSize: 18, display: "block" }}>{usd(td > 0 ? total / td : 0)}</b><span style={{ ...mono, fontSize: 11, color: C.steel }}>per day</span></div>
        </div>
        <div style={{ display: "flex", height: 16, borderRadius: 8, overflow: "hidden", margin: "18px 0 4px", border: `1px solid ${C.border}` }}>
          {rows.filter((r) => r.v > 0).map((r) => <div key={r.k} style={{ width: `${total > 0 ? r.v / total * 100 : 0}%`, background: T_COLORS[rows.indexOf(r) % 7] }} />)}
        </div>
        <div style={{ marginTop: 12 }}>
          {rows.map((r, i) => (
            <div key={r.k} style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 0", borderBottom: `1px solid ${C.linen}`, fontSize: 13 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: T_COLORS[i % 7], flex: "0 0 auto" }} />
              <span style={{ flex: 1 }}>{r.k} <span style={{ ...mono, fontSize: 10, color: C.steel }}>{r.s}</span></span>
              <span style={{ ...mono, fontWeight: 600 }}>{usd(r.v)}</span>
              <span style={{ ...mono, fontSize: 11, color: C.steel, width: 38, textAlign: "right" }}>{total > 0 ? Math.round(r.v / total * 100) : 0}%</span>
            </div>
          ))}
        </div>
        <Btn onClick={save} style={{ width: "100%", marginTop: 16 }}>＋ Save as a trip in Expenses</Btn>
        <div style={{ ...mono, fontSize: 10, color: C.steel, textAlign: "center", marginTop: 10 }}>A forecast — real fares swing ±20% by season & lead time.</div>
      </div>
    </div>
  );
}

function Expenses({ state, setState }) {
  const trips = state.trips.length ? state.trips : seedTrips();
  const active = trips.find((t) => t.id === state.activeTripId) || trips[0];
  const upd = (rid, field, val) => setState((s) => ({
    ...s, trips: s.trips.map((t) => t.id !== active.id ? t : {
      ...t, items: t.items.map((r) => r.id !== rid ? r : { ...r, [field]: (field === "est" || field === "act") ? (val === "" ? "" : +val) : val }),
    }),
  }));
  const addRow = () => setState((s) => ({ ...s, trips: s.trips.map((t) => t.id !== active.id ? t : { ...t, items: [...t.items, mkItem("", "", "", "", "", "")] }) }));
  const delRow = (rid) => setState((s) => ({ ...s, trips: s.trips.map((t) => t.id !== active.id ? t : { ...t, items: t.items.filter((r) => r.id !== rid) }) }));
  const newTrip = () => { const t = { id: uid(), name: "New trip", dest: "", items: [mkItem("", "", "", "", "", "")] }; setState((s) => ({ activeTripId: t.id, trips: [t, ...s.trips] })); };
  const delTrip = () => setState((s) => { const rest = s.trips.filter((t) => t.id !== active.id); const t2 = rest.length ? rest : seedTrips(); return { activeTripId: t2[0].id, trips: t2 }; });

  const est = active.items.reduce((a, i) => a + (+i.est || 0), 0);
  const act = active.items.reduce((a, i) => a + (+i.act || 0), 0);
  const variance = act - est;
  const tripTotal = (t) => t.items.reduce((a, i) => a + (+i.act || +i.est || 0), 0);
  const cell = { width: "100%", background: "transparent", border: "1px solid transparent", color: C.text, borderRadius: 5, padding: "6px 6px", fontSize: 12, fontFamily: FONT_SANS };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "250px 1fr", gap: 16, alignItems: "start" }}>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={label}>Trips</div>
          <Btn variant="ghost" style={{ padding: "6px 10px", fontSize: 10 }} onClick={newTrip}>＋ New</Btn>
        </div>
        {trips.map((t) => (
          <div key={t.id} onClick={() => setState((s) => ({ ...s, activeTripId: t.id }))} style={{
            padding: "11px 13px", borderRadius: 8, cursor: "pointer", marginBottom: 8,
            border: `1px solid ${t.id === active.id ? C.orange : C.border}`,
            background: t.id === active.id ? C.rowHover : C.white,
          }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{t.name}</div>
            <div style={{ ...mono, fontSize: 10, color: C.steel, marginTop: 3 }}>{t.items.length} items · {t.dest || "—"}</div>
            <div style={{ ...mono, fontSize: 15, fontWeight: 700, marginTop: 7 }}>{usd(tripTotal(t))}</div>
          </div>
        ))}
      </div>

      <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 14 }}>
          <Kpi label="Estimated" value={usd2(est)} />
          <Kpi label="Actual spent" value={usd2(act)} tone={C.orangeSoft} />
          <Kpi label="Variance" value={(variance >= 0 ? "+" : "−") + usd2(Math.abs(variance)).slice(1)} tone={variance > 0 ? C.danger : C.success} />
        </div>
        <div style={{ ...card, padding: "14px 16px" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ ...mono, fontSize: 9, color: C.steel, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  {["Date", "Category", "Item / Vendor", "Est", "Actual", "Paid with", ""].map((h, i) => (
                    <th key={i} style={{ textAlign: i === 3 || i === 4 ? "right" : "left", padding: "8px 8px", borderBottom: `1px solid ${C.border}`, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {active.items.map((r) => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${C.linen}` }}>
                    <td style={{ padding: "3px 6px", width: 100 }}><input value={r.date || ""} placeholder="—" onChange={(e) => upd(r.id, "date", e.target.value)} style={{ ...cell, ...mono, fontSize: 11 }} /></td>
                    <td style={{ padding: "3px 6px", width: 110 }}><input value={r.cat || ""} onChange={(e) => upd(r.id, "cat", e.target.value)} style={cell} /></td>
                    <td style={{ padding: "3px 6px" }}><input value={r.item || ""} onChange={(e) => upd(r.id, "item", e.target.value)} style={cell} /></td>
                    <td style={{ padding: "3px 6px", width: 84 }}><input value={r.est ?? ""} placeholder="0" onChange={(e) => upd(r.id, "est", e.target.value)} style={{ ...cell, ...mono, textAlign: "right" }} /></td>
                    <td style={{ padding: "3px 6px", width: 84 }}><input value={r.act ?? ""} placeholder="—" onChange={(e) => upd(r.id, "act", e.target.value)} style={{ ...cell, ...mono, textAlign: "right" }} /></td>
                    <td style={{ padding: "3px 6px", width: 104 }}><input value={r.paid || ""} placeholder="—" onChange={(e) => upd(r.id, "paid", e.target.value)} style={cell} /></td>
                    <td style={{ padding: "3px 6px", textAlign: "right", width: 28 }}><span onClick={() => delRow(r.id)} style={{ cursor: "pointer", color: C.steel, ...mono }}>×</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
            <Btn variant="ghost" style={{ padding: "8px 12px", fontSize: 10 }} onClick={addRow}>＋ Add expense</Btn>
            <span style={{ ...mono, fontSize: 10, color: C.steel }}>{active.items.length} items · saved in your browser</span>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Btn variant="ghost" style={{ color: C.danger, fontSize: 10, padding: "7px 11px" }} onClick={delTrip}>Delete trip</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── primitives ──────────────────────────────────────────────────────────────
const selStyle = {
  fontFamily: FONT_MONO, fontSize: 12, padding: "8px 10px", borderRadius: 4,
  border: `1px solid ${C.border}`, background: C.white, color: C.text, width: "100%",
  boxSizing: "border-box",
};

function Field({ label: l, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ ...mono, fontSize: 10, color: C.steel, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{l}</div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ ...mono, fontSize: 12, color: C.steel, textAlign: "center", padding: "34px 20px" }}>{children}</div>;
}

function Modal({ title, sub, children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,14,26,.45)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, padding: 24, width: "100%", maxWidth: 460, maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontFamily: FONT_SANS, fontSize: 17, fontWeight: 800 }}>{title}</div>
          {sub && <div style={{ ...mono, fontSize: 11, color: C.steel, marginTop: 3 }}>{sub}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}
